import { useEffect, useState } from "react";
import { supAPI, fmtINR } from "./supApi";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { EmptyState } from "./SupplyItems";
import { Modal, Fld, Footer, cls } from "./SupplyStock";
import { NumberInput } from "@/components/ui/number-input";

export default function SupplyGRNs() {
  const [rows, setRows] = useState([]);
  const [pos, setPos] = useState([]);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = () => supAPI.grns().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    supAPI.pos({ status: "sent" }).then(a => supAPI.pos({ status: "partially_received" }).then(b => setPos([...a, ...b])));
    supAPI.stores().then(setStores);
    supAPI.items().then(setItems);
  }, []);

  return (
    <div className="space-y-4" data-testid="sup-grns">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-[16px] font-semibold text-hocp-navy">Goods Received Notes</h2>
          <p className="text-[12.5px] text-slate-500">Record receipts against sent purchase orders. Auto-updates stock.</p>
        </div>
        <button onClick={() => setCreating(true)} disabled={!pos.length}
                data-testid="sup-grn-new"
                className="h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-50">
          <Plus className="w-4 h-4"/> Receive Goods
        </button>
      </div>

      {!pos.length && !rows.length && (
        <EmptyState msg="No open POs to receive against. Create a Purchase Order first."/>
      )}
      {!rows.length && pos.length > 0 && (
        <EmptyState msg="No receipts yet. Click Receive Goods to record your first GRN."/>
      )}
      {rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11.5px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">GRN</th>
                <th className="text-left px-4 py-3">Against PO</th>
                <th className="text-left px-4 py-3">Vendor</th>
                <th className="text-left px-4 py-3">Store</th>
                <th className="text-right px-4 py-3">Lines</th>
                <th className="text-left px-4 py-3">Received</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr key={r.id} data-testid={`sup-grn-row-${r.id}`} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3 mono text-slate-500">{r.code}</td>
                  <td className="px-4 py-3 mono text-slate-500">{r.po_code}</td>
                  <td className="px-4 py-3 font-medium text-hocp-navy">{r.vendor_name}</td>
                  <td className="px-4 py-3 text-slate-600">{r.store_name}</td>
                  <td className="px-4 py-3 text-right">{(r.items || []).length}</td>
                  <td className="px-4 py-3 text-slate-500">{(r.received_at || "").replace("T", " ").slice(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <GRNCreateDialog pos={pos} stores={stores} items={items}
                                     onClose={() => setCreating(false)}
                                     onDone={() => { setCreating(false); load();
                                       supAPI.pos({ status: "sent" }).then(a => supAPI.pos({ status: "partially_received" }).then(b => setPos([...a, ...b])));
                                     }}/>}
    </div>
  );
}

function GRNCreateDialog({ pos, stores, items, onClose, onDone }) {
  const [poId, setPoId] = useState(pos[0]?.id || "");
  const [storeId, setStoreId] = useState(stores[0]?.id || "");
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const itemMap = Object.fromEntries(items.map(i => [i.id, i]));

  useEffect(() => {
    const po = pos.find(p => p.id === poId);
    if (po) {
      setLines(po.items.map(li => ({ item_id: li.item_id, qty_received: li.qty, unit_cost: li.unit_cost })));
    } else {
      setLines([]);
    }
  }, [poId, pos]);

  const update = (i, patch) => setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const submit = async () => {
    if (!poId || !storeId) { toast.error("PO and store required"); return; }
    const valid = lines.filter(l => Number(l.qty_received) > 0);
    if (!valid.length) { toast.error("Enter at least one qty > 0"); return; }
    setSaving(true);
    try {
      await supAPI.createGRN({
        po_id: poId, store_id: storeId,
        items: valid.map(l => ({ item_id: l.item_id,
                                  qty_received: Number(l.qty_received),
                                  unit_cost: Number(l.unit_cost || 0) })),
        notes: notes || undefined,
      });
      toast.success("Goods received — stock updated"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Receive Goods (GRN)" onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Against PO">
            <select value={poId} onChange={e => setPoId(e.target.value)} data-testid="grn-po" className={cls}>
              {pos.map(p => <option key={p.id} value={p.id}>{p.code} · {p.vendor_name} · {fmtINR(p.total_amount)}</option>)}
            </select>
          </Fld>
          <Fld label="Receive into store">
            <select value={storeId} onChange={e => setStoreId(e.target.value)} data-testid="grn-store" className={cls}>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Fld>
        </div>
        {!!lines.length && (
          <div>
            <div className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium mb-2">Lines</div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Item</th>
                    <th className="text-right px-3 py-2">Qty Received</th>
                    <th className="text-right px-3 py-2">Unit ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2">{itemMap[l.item_id]?.name || l.item_id}</td>
                      <td className="px-3 py-2">
                        <NumberInput value={l.qty_received} onChange={e => update(i, { qty_received: e.target.value })}
                               data-testid={`grn-qty-${i}`}
                               className="w-24 h-8 px-2 rounded-md border border-slate-200 text-right text-[13px] tabular-nums float-right"/>
                      </td>
                      <td className="px-3 py-2">
                        <NumberInput value={l.unit_cost} onChange={e => update(i, { unit_cost: e.target.value })}
                               className="w-24 h-8 px-2 rounded-md border border-slate-200 text-right text-[13px] tabular-nums float-right"/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <Fld label="Notes (optional)" full>
          <input value={notes} onChange={e => setNotes(e.target.value)} className={cls}/>
        </Fld>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="grn-submit"/>
    </Modal>
  );
}
